import { useDebounce } from "@/utils";
import {
  Badge,
  Box,
  Button,
  HStack,
  Image,
  Input,
  List,
  ListItem,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Agreement } from "@partiu-vantagens/partiu-sdk/dist/types/agreement";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  canDocumentBeReferred,
  createReferral,
  getReferrableAgreements,
} from "../../api/referral";
import { InputMask } from "../atoms/InputMask";
import { LabeledInput } from "./LabeledInput";

type ReferralAgreementSelectProps = {
  onChange: (agreement: Agreement) => void;
};

function ReferralAgreementSelect({ onChange }: ReferralAgreementSelectProps) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["referrable-agreements"],
    queryFn: () => getReferrableAgreements(),
  });

  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<Agreement | null>(null);

  const filtered = useMemo(
    () =>
      data.filter((a) =>
        a.name.toLowerCase().includes(search.toLowerCase())
      ),
    [data, search]
  );

  function handleSelect(agreement: Agreement) {
    setSelected(agreement);
    setSearch(agreement.name);
    setIsOpen(false);
    onChange(agreement);
  }

  return (
    <Box width="100%" position="relative">
      <Input
        placeholder={
          isLoading
            ? "Carregando convênios..."
            : data.length === 0
            ? "Sem ofertas de indicação no momento"
            : "Buscar convênio..."
        }
        value={search}
        isDisabled={isLoading || data.length === 0}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
          if (selected && e.target.value !== selected.name) {
            setSelected(null);
          }
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        autoComplete="off"
      />
      {isOpen && filtered.length > 0 && (
        <List
          position="absolute"
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="md"
          width="100%"
          maxH="220px"
          overflowY="auto"
          mt={1}
        >
          {filtered.map((a) => (
            <ListItem
              key={a.id}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              fontWeight={selected?.id === a.id ? "bold" : "normal"}
              onMouseDown={() => handleSelect(a)}
            >
              {a.name}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

type ReferralAgreementDataProps = {
  agreement: Agreement;
};

function ReferralAgreementData({ agreement }: ReferralAgreementDataProps) {
  const referralValue = (agreement as any).referral_value;
  return (
    <VStack width="100%">
      {agreement.referral_image && (
        <Image
          src={agreement.referral_image}
          objectFit="contain"
          width="100%"
          height="150px"
        />
      )}
      {referralValue && (
        <HStack width="100%" justify="flex-start">
          <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
            Ganhe R$ {Number(referralValue).toFixed(2).replace(".", ",")} por indicação confirmada
          </Badge>
        </HStack>
      )}
      <Text
        textAlign="justify"
        width="100%"
        dangerouslySetInnerHTML={{
          __html:
            agreement.referral_offer ||
            "Selecione um convênio acima para ver seus dados de indicação.",
        }}
      />
    </VStack>
  );
}

type ReferralDocumentValidatorProps = {
  onValidated: (document: string | null) => void;
  agreementId: number;
};

function ReferralDocumentValidator({
  onValidated,
  agreementId,
}: ReferralDocumentValidatorProps) {
  const [value, setValue] = useState("");
  const rawDocument = useMemo(() => value.replace(/\D/g, ""), [value]);
  const [isValid, setValid] = useState<boolean | undefined>(undefined);
  const inputData = useMemo(() => {
    if (isValid === undefined)
      return {
        detail:
          "Os dados serão validados internamente e retornaremos se o CPF/CNPJ é válido para indicação.",
      };

    if (isValid)
      return {
        borderColor: "green",
        detail:
          "CPF/CNPJ é válido e pode ser indicado. Clique no botão abaixo para prosseguir.",
      };

    return {
      borderColor: "red",
      detail: "CPF/CNPJ é inválido ou não pode ser indicado.",
    };
  }, [isValid]);
  const checkDocumentMutation = useMutation({
    mutationFn: ({
      agreementId,
      document,
    }: {
      agreementId: number;
      document: string;
    }) => canDocumentBeReferred(agreementId, document),
    onSuccess() {
      onValidated(value);
      setValid(true);
    },
    onError() {
      setValid(false);
    },
  });
  const validateDocument = useDebounce((value: string) => {
    onValidated(null);
    setValid(undefined);
    if (![11, 13].includes(rawDocument.length)) return;
    checkDocumentMutation.mutate({ agreementId, document: value });
  }, 500);

  const isCNPJ = rawDocument.length > 11;
  const mask = isCNPJ ? "99.999.999/9999-99" : "999.999.999-999";

  useEffect(() => {
    validateDocument(value);
  }, [value]);

  return (
    <LabeledInput
      label="CPF/CNPJ *"
      value={value}
      Component={InputMask}
      onChange={(e) => setValue(e.target.value)}
      mask={mask}
      isDisabled={checkDocumentMutation.isPending}
      maskChar={null}
      {...inputData}
    />
  );
}

const EMPTY_FORM = {
  document: "",
  isValid: undefined as boolean | undefined,
  name: "",
  telephone: "",
  email: "",
};

export function ReferrerForm({ onClose }: { onClose: () => void }) {
  const [agreement, setAgreement] = useState<Agreement | undefined>(undefined);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [validatorKey, setValidatorKey] = useState(0);

  const toast = useToast();
  const queryClient = useQueryClient();

  const submitMutation = useMutation({
    mutationFn: () =>
      createReferral({
        agreement: agreement!.id,
        document: formData.document,
        name: formData.name || undefined,
        telephone: formData.telephone || undefined,
        email: formData.email || undefined,
      }),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
      toast({ title: "Indicação enviada com sucesso!", status: "success", duration: 4000 });
      setFormData(EMPTY_FORM);
      setValidatorKey((k) => k + 1);
    },
    onError() {
      toast({ title: "Erro ao enviar indicação.", status: "error", duration: 4000 });
    },
  });

  return (
    <VStack>
      <VStack width="100%" gap={4}>
        <ReferralAgreementSelect
          onChange={(a) => {
            setAgreement(a);
            setFormData(EMPTY_FORM);
            setValidatorKey((k) => k + 1);
          }}
        />
        {agreement && (
          <>
            <ReferralAgreementData agreement={agreement} />
            <ReferralDocumentValidator
              key={validatorKey}
              agreementId={agreement.id}
              onValidated={(doc) =>
                setFormData((prev) => ({ ...prev, document: doc ?? "", isValid: doc !== null }))
              }
            />
            <Input
              placeholder="Nome (opcional)"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            />
            <Input
              placeholder="Telefone (opcional)"
              value={formData.telephone}
              onChange={(e) => setFormData((prev) => ({ ...prev, telephone: e.target.value }))}
            />
            <Input
              placeholder="E-mail (opcional)"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            />
          </>
        )}
      </VStack>

      <HStack mt={8} width="100%">
        <Button width="100%" variant="ghost" mr={3} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          isDisabled={!formData.isValid}
          isLoading={submitMutation.isPending}
          width="100%"
          colorScheme="green"
          onClick={() => submitMutation.mutate()}
        >
          Indicar
        </Button>
      </HStack>
    </VStack>
  );
}
